import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, FileText, Pencil, Trash2, Send, CheckCircle2,
  XCircle, ArrowRight, Building2, Calendar, AlertCircle, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useQuote, useUpdateQuoteStatus, useDeleteQuote, useConvertQuoteToOrder,
} from '@/hooks/useSales';
import {
  LINE_ITEM_KIND_LABELS, type LineItem, type QuoteStatus,
} from '@/types/sales';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { QuoteStatusBadge } from '@/components/quotes/StatusBadges';
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
import { TotalsPanel } from '@/components/quotes/TotalsPanel';
import { formatCurrency } from '@/lib/utils';

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'quotes', 'write') : false;
  const showCost = user ? can(user.role, 'financials', 'read') : false;

  const { data: quote, isLoading, error } = useQuote(id);
  const updateStatus = useUpdateQuoteStatus();
  const deleteQuote = useDeleteQuote();
  const convert = useConvertQuoteToOrder();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [poNumber, setPoNumber] = useState('');

  if (isLoading) return <LoaderScreen />;
  if (error || !quote) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load quote"
          description={(error as { message?: string })?.message ?? 'Quote not found.'}
          action={<Link to="/quotes"><Button variant="secondary" leftIcon={<ArrowLeft size={14} />}>Back to Quotes</Button></Link>}
        />
      </Card>
    );
  }

  async function setStatus(s: QuoteStatus) {
    if (!quote) return;
    try {
      await updateStatus.mutateAsync({ id: quote.id, status: s });
      toast.success(`Status → ${s}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Status update failed.');
    }
  }

  async function onConvert() {
    if (!quote) return;
    try {
      const result = await convert.mutateAsync({ id: quote.id, poNumber: poNumber.trim() || undefined });
      toast.success(`Converted → ${result.number}`);
      setConvertOpen(false);
      navigate(`/orders/${result.id}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Conversion failed.');
    }
  }

  async function onDelete() {
    if (!quote) return;
    try {
      await deleteQuote.mutateAsync(quote.id);
      toast.success(`Deleted ${quote.number}`);
      navigate('/quotes');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-8">
      {/* Back */}
      <Link to="/quotes" className="inline-flex items-center gap-2 text-xs text-text-dim hover:text-text-muted transition-colors">
        <ArrowLeft size={12} />
        <span className="kicker">All Quotes</span>
      </Link>

      {/* Hero */}
      <header className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-5 min-w-0 flex-1">
          <div className="shrink-0 h-16 w-16 rounded-sm bg-walnut-700 border border-brass-400/30 flex items-center justify-center">
            <FileText size={22} className="text-brass-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="kicker">Quote</span>
              <QuoteStatusBadge status={quote.status} />
            </div>
            <h1 className="font-display text-4xl text-text leading-tight tracking-tight font-mono">
              {quote.number}
            </h1>
            {quote.projectName && (
              <p className="text-text-muted mt-2 text-sm">{quote.projectName}</p>
            )}
          </div>
        </div>

        {canWrite && (
          <div className="flex items-center gap-2 shrink-0">
            {quote.status === 'draft' && (
              <Button variant="secondary" leftIcon={<Send size={13} />} onClick={() => setStatus('sent')} loading={updateStatus.isPending}>
                Mark Sent
              </Button>
            )}
            {quote.status === 'sent' && (
              <>
                <Button variant="secondary" leftIcon={<XCircle size={13} />} onClick={() => setStatus('rejected')} loading={updateStatus.isPending}>
                  Mark Rejected
                </Button>
                <Button leftIcon={<CheckCircle2 size={13} />} onClick={() => setStatus('accepted')} loading={updateStatus.isPending}>
                  Mark Accepted
                </Button>
              </>
            )}
            {quote.status === 'accepted' && (
              <Button leftIcon={<ArrowRight size={13} />} onClick={() => setConvertOpen(true)}>
                Convert to Order
              </Button>
            )}
            {quote.status === 'converted' && quote.convertedOrderId && (
              <Link to={`/orders/${quote.convertedOrderId}`}>
                <Button variant="secondary" rightIcon={<ArrowRight size={13} />}>
                  View Order
                </Button>
              </Link>
            )}
            <Button variant="secondary" leftIcon={<Pencil size={13} />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Delete">
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </header>

      {/* Meta strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Meta label="Client" value={
          <Link to={`/clients/${quote.clientId}`} className="text-text hover:text-brass-300 inline-flex items-center gap-1.5">
            <Building2 size={12} />
            {quote.clientName ?? quote.clientId}
          </Link>
        } />
        <Meta label="Issued" value={quote.issueDate ? new Date(quote.issueDate).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—'} />
        <Meta label="Expires" value={quote.expirationDate ? new Date(quote.expirationDate).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—'} />
        <Meta label="Total" value={formatCurrency(quote.totals?.total ?? 0)} accent />
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Line items */}
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border">
            <div className="kicker mb-1">Line Items · {quote.lineItems.length}</div>
            <h3 className="font-display text-lg text-text">Specifications</h3>
          </header>

          {quote.lineItems.length === 0 ? (
            <EmptyState icon={FileText} title="No line items" description="Edit this quote to add specifications." />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="kicker px-6 py-3 font-normal w-12">#</th>
                  <th className="kicker px-6 py-3 font-normal">Item</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Qty</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Unit Price</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {quote.lineItems.map((it, idx) => (
                  <LineItemReadRow key={it.id} item={it} index={idx} />
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Totals */}
        <div className="space-y-4">
          <TotalsPanel
            lineItems={quote.lineItems}
            discountPct={quote.discountPct}
            taxRatePct={quote.taxRatePct}
            shipping={quote.shipping}
            showMargin={showCost}
            readonly
          />

          {quote.notes && (
            <Card>
              <div className="kicker mb-2">Notes</div>
              <p className="text-sm text-text-muted whitespace-pre-wrap leading-relaxed">{quote.notes}</p>
            </Card>
          )}

          {quote.internalNotes && canWrite && (
            <Card className="border-warning/30">
              <div className="flex items-center gap-2 mb-2">
                <div className="kicker text-warning">Internal Only</div>
              </div>
              <p className="text-sm text-text-muted whitespace-pre-wrap leading-relaxed">{quote.internalNotes}</p>
            </Card>
          )}

          {quote.termsAndConditions && (
            <Card>
              <div className="kicker mb-2">Terms & Conditions</div>
              <p className="text-xs text-text-dim whitespace-pre-wrap leading-relaxed">{quote.termsAndConditions}</p>
            </Card>
          )}
        </div>
      </div>

      {/* Activity */}
      <Card>
        <div className="kicker mb-4">Activity</div>
        <ol className="space-y-3 text-sm">
          {quote.createdAt && (
            <ActivityLine icon={Calendar} text="Created" timestamp={quote.createdAt} />
          )}
          {quote.sentAt && (
            <ActivityLine icon={Mail} text="Sent to client" timestamp={quote.sentAt} />
          )}
          {quote.acceptedDate && (
            <ActivityLine icon={CheckCircle2} text="Accepted" timestamp={quote.acceptedDate} accent />
          )}
          {quote.convertedOrderId && (
            <ActivityLine icon={ArrowRight} text={
              <>Converted to order <Link to={`/orders/${quote.convertedOrderId}`} className="text-brass-300 hover:text-brass-200">↗</Link></>
            } timestamp={quote.updatedAt} accent />
          )}
        </ol>
      </Card>

      {/* Modals */}
      <QuoteFormModal open={editOpen} onClose={() => setEditOpen(false)} quote={quote} />

      <Modal
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        kicker="Convert"
        title="Convert quote to work order."
        description="Creates a new order with the same line items, totals, and client. The quote will be marked as Converted."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConvertOpen(false)} disabled={convert.isPending}>Cancel</Button>
            <Button onClick={onConvert} loading={convert.isPending} leftIcon={<ArrowRight size={13} />}>
              Create Order
            </Button>
          </>
        }
      >
        <div className="py-2">
          <Input
            label="Client PO Number"
            placeholder="Optional — e.g. PO-2024-1248"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            hint="If you have a PO from the client, enter it here for reference."
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        loading={deleteQuote.isPending}
        destructive
        title={`Delete ${quote.number}?`}
        confirmLabel="Delete quote"
        description="This permanently removes the quote. If it has been converted, the resulting order remains."
      />
    </div>
  );
}

function Meta({ label, value, accent }: { label: string; value: React.ReactNode; accent?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-1.5">{label}</div>
      <div className={`text-sm tabular ${accent ? 'font-display text-2xl text-brass-300' : 'text-text'}`}>{value}</div>
    </div>
  );
}

function LineItemReadRow({ item, index }: { item: LineItem; index: number }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-bg-hover/30 transition-colors">
      <td className="px-6 py-4 align-top text-text-dim tabular">{String(index + 1).padStart(2, '0')}</td>
      <td className="px-6 py-4">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span className="kicker text-brass-300">{LINE_ITEM_KIND_LABELS[item.kind]}</span>
          {item.sku && <span className="text-2xs font-mono text-text-dim">#{item.sku}</span>}
        </div>
        <div className="text-text">{item.description}</div>
        {(item.veneerSpecies || item.finish || item.dimensions) && (
          <div className="text-xs text-text-dim mt-1 flex items-center gap-3 flex-wrap">
            {item.veneerSpecies && <span>{item.veneerSpecies}</span>}
            {item.finish && <span>· {item.finish}</span>}
            {item.dimensions && <span>· {item.dimensions}</span>}
          </div>
        )}
      </td>
      <td className="px-6 py-4 text-right tabular text-text whitespace-nowrap">
        {item.quantity} {item.unit ?? 'ea'}
      </td>
      <td className="px-6 py-4 text-right tabular text-text-muted">
        {formatCurrency(item.unitPrice)}
      </td>
      <td className="px-6 py-4 text-right tabular text-text font-medium">
        {formatCurrency(item.quantity * item.unitPrice)}
      </td>
    </tr>
  );
}

function ActivityLine({
  icon: Icon, text, timestamp, accent,
}: {
  icon: typeof Calendar;
  text: React.ReactNode;
  timestamp?: string;
  accent?: boolean;
}) {
  return (
    <li className="flex items-center gap-3 text-sm">
      <div className={`h-6 w-6 shrink-0 rounded-sm flex items-center justify-center ${
        accent ? 'bg-brass-400/15 border border-brass-400/40' : 'bg-bg-subtle border border-border'
      }`}>
        <Icon size={11} className={accent ? 'text-brass-300' : 'text-text-dim'} />
      </div>
      <div className="text-text">{text}</div>
      {timestamp && (
        <span className="text-xs text-text-dim tabular ml-auto">
          {new Date(timestamp).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      )}
    </li>
  );
}
